import { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import * as trackingActions from 'dux/tracking/actions';

/*
 * Track page content visibility using sentinels and IntersectionObserver
 * Props:
 *   trigOnce: trig event for threshold visibility only once
 *   trigDirection: trig only if scroll is downward, upward or both
 */
const ScrollTracker = ({ trigOnce, trigDirection, trackScroll, children }) => {
  const sentinels = useRef([]);
  const [observer, setObserver] = useState();
  const triggered = {};
  // thresholds of scroll values to track
  const thresholds = [25, 50, 75, 95];

  const observeScroll = useCallback(() => {
    if (observer) {
      const height = document.body.scrollHeight;
      thresholds.forEach((threshold, index) => {
        const sentinel = document.createElement('div');
        sentinel.style.position = 'absolute';
        sentinel.style.top = `${(height * threshold) / 100}px`;
        sentinel._threshold = threshold;
        sentinel._index = index;
        sentinels.current.push(sentinel);
        document.body.appendChild(sentinel);
        observer?.observe(sentinel);
      });
    }
  }, [observer]);

  useEffect(() => {
    const newObserver = new IntersectionObserver(entries =>
      entries.forEach(entry => {
        if (!entry.isIntersecting) {
          return;
        }
        const value = entry.target._threshold;
        if (trigOnce && value in triggered) {
          return;
        }
        const index = entry.target._index;
        if (trigDirection !== 'both' && index > 0) {
          const prev = thresholds[index - 1];
          if (!(prev in triggered)) {
            return;
          }
        }

        // send scrollDepth event to Heap and GTM
        trackScroll(value);
        triggered[value] = true;
      })
    );
    setObserver(newObserver);
  }, []);

  useEffect(() => {
    return () => {
      sentinels.current.forEach(sentinel => {
        observer?.unobserve(sentinel);
        document.body.removeChild(sentinel);
      });
    };
  }, []);

  useEffect(() => {
    observeScroll();
  }, [observer]);

  return children;
};

ScrollTracker.defaultProps = {
  trigDirection: 'down',
  trigOnce: true,
};

ScrollTracker.propTypes = {
  trigDirection: PropTypes.oneOf(['down', 'up', 'both']),
  trigOnce: PropTypes.bool,
  children: PropTypes.node.isRequired,
  trackScroll: PropTypes.func.isRequired,
};

const mapDispatchToProps = { trackScroll: trackingActions.trackScroll };

export default connect(null, mapDispatchToProps)(ScrollTracker);
